<template>
  <admin-page-shell
    :title="$t('frontend_groovemail_admin_verification_email_content')"
    :subtitle="$t('frontend_groovemail_admin_configure_verification_email')"
  >
    <div class="gm-admin-tab__stack">
      <gs-field class="gm-admin-form" :label="$t('frontend_groovemail_admin_verify_url_text')">
        <text-input
          variant="base"
          v-model="model.verifyUrlText"
          :placeholder="this.$t('frontend_groovemail_admin_enter_verify_url_text')"
          :validator="$v.model.verifyUrlText"
        />
      </gs-field>
      <div class="gm-admin-tab__editor">
        <email-basic-editor
          ref="verification-content-email-editor"
          :editorOptions="verificationEmailEditorOptions"
          :editorContent="model.verificationContent"
          :editorNodeWithNoMargin="true"
          :editorWithBlockedLinkRestriction="true"
          :editorDynamicVariables="dynamicVariables"
          :editorClasses="editorClasses"
          openedFrom="admin"
          :allowEditorPreview="true"
          :allowEditorHtmlView="true"
          @editorContentUpdated="model.verificationContent = $event"
        ></email-basic-editor>
      </div>
      <p
        v-if="
          dataSaved &&
          ((this.model.verificationContent &&
            !this.model.verificationContent.includes('{verify_url}')) ||
            !this.model.verificationContent)
        "
        class="gm-admin-tab__error"
      >
        {{ this.$t('frontend_groovemail_admin_verify_url_include_text') }}
      </p>
    </div>
    <div class="gm-admin-page__footer">
      <gs-button variant="outline" @click="resetVerificationEmailLayout()">{{ this.$t('frontend_reset') }}</gs-button>
      <gs-button variant="primary" @click="updateVerificationEmailLayout()">{{ this.$t('frontend_save') }}</gs-button>
    </div>
  </admin-page-shell>
</template>

<script>
import { required, maxLength, minLength } from 'vuelidate/lib/validators';
import { mapActions } from 'vuex';
import { adminUrls } from '@/utils/urls.js';
import { getData, postData } from '@/utils/apiHelper.js';
import EmailBasicEditor from '@/components/EmailBasicEditor.vue';
import AdminPageShell from '../AdminPageShell.vue';

export default {
  data() {
    return {
      model: {
        verifyUrlText: '',
        verificationContent: '',
      },
      dataSaved: false,
      verificationEmailEditorOptions: {
        modules: {
          toolbar: {
            container: [
              ['bold', 'italic', 'underline', 'strike'], // toggled buttons
              [{ list: 'ordered' }, { list: 'bullet' }],

              ['link', 'image'],
              [{ size: ['10px', false, '18px', '32px'] }], // custom dropdown
              [{ header: [1, 2, 3, 4, 5, 6, false] }],

              [{ color: [] }, { background: [] }], // dropdown with defaults from theme
              [{ align: [] }],
            ],
            handlers: {
              image: () => this.handleImageInsertionInContent()
            }
          },
        },
      },
    };
  },

  validations: {
    model: {
      verifyUrlText: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(20),
      },
      verificationContent: {
        required,
      },
    },
  },

  components: {
    AdminPageShell,
    EmailBasicEditor,
  },

  methods: {
    ...mapActions('loader', ['LOADING']),

    // show loader
    showLoader() {
      this.LOADING(true);
    },

    // hide loader
    hideLoader (){
      this.LOADING(false);
    },

    // Triggered when Image button is clicked on editor, opens our custom image picker
    handleImageInsertionInContent() {
      this.$refs['verification-content-email-editor'].handleImageInsertionInContent(); 
    },

    // Used to reset verification content
    async resetVerificationEmailLayout() {
      this.dataSaved = false;
      this.model.verifyUrlText = '';
      this.model.verificationContent = '';
      this.$v.model.verifyUrlText.$reset();
      this.$v.model.verificationContent.$reset();
      await postData(adminUrls.verificationMailUrl.updateVerificationMailContent, {
        verifyEmailContent: this.model.verificationContent,
        verifyUrlText: this.model.verifyUrlText,
        reset: 1,
      });
    },

    // Used to update verification content
    async updateVerificationEmailLayout() {
      this.dataSaved = true;
      this.$v.$touch();
      const dataValid =
        !this.$v.$invalid &&
        this.model.verificationContent &&
        this.model.verificationContent.includes('{verify_url}');
      if (dataValid) {
        await postData(adminUrls.verificationMailUrl.updateVerificationMailContent, {
          verifyEmailContent: this.model.verificationContent,
          verifyUrlText: this.model.verifyUrlText,
        });
      }
    },

    // Used to fetch verification content data
    async fetchVerificationContent() {
      // show loader
      this.showLoader();

      const response = await getData(adminUrls.verificationMailUrl.getVerificationMailData, null, true);
      if (response && response.data && response.data.verify_content) {
        this.model.verificationContent = response.data.verify_content.value;
        this.model.verifyUrlText = response.data.verify_content.name;
      }
      
      // hide loader
      this.hideLoader();
    }
  },

  computed: {
    /**
     * Returns dynamic variables data for editor props.
     * @return {array} - dynamic variables array
     */
    dynamicVariables() {
      return [
        {
          label: this.$t('frontend_groovemail_admin_user_sender_user_name'),
          value: '{user_name}',
        },
        {
          label: this.$t('frontend_groovemail_admin_verify_url'),
          value: '{verify_url}',
        },
      ];
    },

    /**
     * Returns class for editor label.
     * @return {string} label class
     */
    editorClasses() {
      return {
        labelClasses: 'font-semibold text-sm text-grey-700',
      };
    },
  },

  // Makes API call to fetch verifiction content before DOM load
  async created() {
    await this.fetchVerificationContent();
  }
};
</script>
